import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import apiService from '../../services/apiService';

import PredictionMetadata from './PredictionMetadata';
import PredictionTrends from './PredictionTrends';
import ProbabilityDistribution from './ProbabilityDistribution';
import ABTestingPanel from './ABTestingPanel';
import MetricsPanel from './MetricsPanel';

import { formatDate, balanceMap } from './util';
import { TRAINING_RUNS_BASE_URL } from '../../constants/constants';

// Metrics mapping
const plotTypeMap = {
  roc: 'roc_curve',
  'precision-recall': 'pr_curve',
  confusion: 'confusion_matrix',
  'prediction-distribution': 'prediction_distribution',
};

function ModelDetails() {
  const { modelId } = useParams();

  /* ===================== STATE ===================== */

  const [model, setModel] = useState(null);

  // Trends
  const [trendRange, setTrendRange] = useState('day');
  const [predictionTrends, setPredictionTrends] = useState([]);
  const [loadingTrends, setLoadingTrends] = useState(false);

  // Distribution
  const [distribution, setDistribution] = useState([]);
  const [distributionMode, setDistributionMode] = useState('prediction');
  const [scaleMode, setScaleMode] = useState('linear');
  const [distributionLoading, setDistributionLoading] = useState(false);

  // Metadata (sada uključuje latency)
  const [metadata, setMetadata] = useState(null);

  // A/B testing
  const [abTestConfig, setAbTestConfig] = useState(null);
  const [abTestResults, setAbTestResults] = useState([]);
  const [abStatsLoading, setAbStatsLoading] = useState(false);
  const [abConfirmOpen, setAbConfirmOpen] = useState(false);
  const [abPendingAction, setAbPendingAction] = useState(null);

  // Metrics
  const [trainingId, setTrainingId] = useState(null);
  const [plotType, setPlotType] = useState('roc');
  const [metricsData, setMetricsData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  /* ===================== EFFECTS ===================== */

  // Fetch model
  useEffect(() => {
    if (!modelId) return;
    apiService.fetchModelById(modelId).then((res) => {
      console.log('Model response:', res);
      if (res?.success) setModel(res.data);
    });
  }, [modelId]);

  // Fetch training request
  useEffect(() => {
    if (!modelId) return;
    apiService.fetchTrainingRequestByModelId(modelId).then((res) => {
      console.log('Training request response:', res);
      if (res?.success) setTrainingId(res.data.unique_id);
    });
  }, [modelId]);

  // Fetch metadata + latency
  useEffect(() => {
    if (!modelId) return;

    const fetchMetadata = async () => {
      try {
        // fetch model info (total predictions)
        const modelRes = await apiService.fetchModelById(modelId);
        const modelData = modelRes?.success ? modelRes.data : {};

        // fetch latency
        const latencyRes = await apiService.fetchModelLatency(modelId);
        const medianLatency =
          latencyRes?.success && latencyRes.data?.median_latency_ms
            ? latencyRes.data.median_latency_ms
            : modelData.median_latency ?? 0;

        // fetch prediction trends/counts za class 0/1 i time range
        const trendsRes = await apiService.fetchPredictionTrends(
          modelId,
          'day'
        ); // default range
        const periods = trendsRes?.success ? trendsRes.data?.periods ?? [] : [];

        const totalCounts = periods.reduce(
          (acc, p) => {
            acc.class_0 += p.class_0_count ?? 0;
            acc.class_1 += p.class_1_count ?? 0;
            return acc;
          },
          { class_0: 0, class_1: 0 }
        );

        // Time range used from API
        const timeRange = {
          start: formatDate(trendsRes?.data?.time_range?.start),
          end: formatDate(trendsRes?.data?.time_range?.end),
        };

        // 4️⃣ set state
        setMetadata({
          total_count: modelData.total_predictions ?? 0,
          predictions_summary: totalCounts,
          time_range: timeRange,
          threshold: 0.5, // default
          median_latency: medianLatency,
        });
      } catch (err) {
        console.error('Error fetching metadata:', err);
        setMetadata({
          total_count: 0,
          predictions_summary: { class_0: 0, class_1: 0 },
          time_range: { start: 'N/A', end: 'N/A' },
          threshold: 0.5,
          median_latency: 0,
        });
      }
    };

    fetchMetadata();
  }, [modelId]);

  // Prediction trends
  useEffect(() => {
    if (!modelId) return;
    setLoadingTrends(true);

    apiService
      .fetchPredictionTrends(modelId, trendRange)
      .then((res) => {
        if (!res?.success) return;

        const periods = res.data?.periods ?? [];
        const formatted = periods.map((p) => ({
          timestamp: p.period_label,
          class_0: p.class_0_count ?? 0,
          class_1: p.class_1_count ?? 0,
          total: (p.class_0_count ?? 0) + (p.class_1_count ?? 0),
        }));

        setPredictionTrends(formatted);

        setMetadata((prev) => ({
          ...prev,
          total_count: res.data.total_count,
          predictions_summary: {
            class_0: formatted.reduce((s, p) => s + p.class_0, 0),
            class_1: formatted.reduce((s, p) => s + p.class_1, 0),
          },
          time_range: {
            start: formatDate(res.data.time_range?.start),
            end: formatDate(res.data.time_range?.end),
          },
        }));
      })
      .finally(() => setLoadingTrends(false));
  }, [modelId, trendRange]);

  // Probability distribution
  useEffect(() => {
    if (!modelId) return;
    setDistributionLoading(true);

    apiService
      .fetchProbabilityDistribution(modelId, distributionMode)
      .then((res) => {
        if (!res?.success) return;
        const bins = res.data?.bins ?? [];
        setDistribution(
          bins.map((b) => ({
            bucket: b.bin_label,
            count: b.count,
          }))
        );
      })
      .finally(() => setDistributionLoading(false));
  }, [modelId, distributionMode]);

  // A/B testing stats
  useEffect(() => {
    if (!modelId) return;
    setAbStatsLoading(true);

    apiService
      .fetchABTestStats(modelId)
      .then((res) => {
        if (!res?.success) return;

        const d = res.data;
        setAbTestResults([
          {
            group: 'Test',
            ReachedTarget:
              d.test_total > 0
                ? (d.test_reached_target / d.test_total) * 100
                : 0,
            NotReachedTarget:
              d.test_total > 0
                ? (d.test_not_reached_target / d.test_total) * 100
                : 0,
          },
          {
            group: 'Prediction',
            ReachedTarget:
              d.prediction_total > 0
                ? (d.prediction_reached_target / d.prediction_total) * 100
                : 0,
            NotReachedTarget:
              d.prediction_total > 0
                ? (d.prediction_not_reached_target / d.prediction_total) * 100
                : 0,
          },
        ]);

        setAbTestConfig({
          enabled: d.config?.enabled ?? false,
          trafficMode: d.config?.trafficMode ?? '50-50',
          predictPercent: d.config?.predictPercent ?? 50,
          testPercent: d.config?.testPercent ?? 50,
        });
      })
      .finally(() => setAbStatsLoading(false));
  }, [modelId]);

  // Metrics
  useEffect(() => {
    if (!trainingId || !plotType) return;

    const file = plotTypeMap[plotType];
    const url = `${TRAINING_RUNS_BASE_URL}${trainingId}/results/evaluation_data/${file}.json`;

    setMetricsLoading(true);
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then(setMetricsData)
      .finally(() => setMetricsLoading(false));
  }, [trainingId, plotType]);

  /* ===================== RENDER ===================== */

  return (
    <Box p={3}>
      <Typography variant='h4' fontWeight='bold' mb={3}>
        {model?.model_name ?? 'Loading model...'}
      </Typography>

      <PredictionMetadata metadata={metadata} loading={!metadata} />

      <PredictionTrends
        predictionTrends={predictionTrends}
        loadingTrends={loadingTrends}
        trendRange={trendRange}
        setTrendRange={setTrendRange}
      />

      <ProbabilityDistribution
        distribution={distribution}
        distributionMode={distributionMode}
        setDistributionMode={setDistributionMode}
        scaleMode={scaleMode}
        setScaleMode={setScaleMode}
        distributionLoading={distributionLoading}
      />

      <ABTestingPanel
        abTestConfig={abTestConfig}
        setAbTestConfig={setAbTestConfig}
        abConfirmOpen={abConfirmOpen}
        setAbConfirmOpen={setAbConfirmOpen}
        abPendingAction={abPendingAction}
        setAbPendingAction={setAbPendingAction}
        handleConfirmABConfig={() => {}}
        statsData={abTestResults}
        statsLoading={abStatsLoading}
      />

      <MetricsPanel
        plotType={plotType}
        setPlotType={setPlotType}
        metricsData={metricsData}
        metricsLoading={metricsLoading}
      />
    </Box>
  );
}

export default ModelDetails;
